<template>
    <main class="flex-1 relative overflow-y-auto py-6 focus:outline-none" tabindex="0">
        <form @submit.prevent="saveProduct">
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 px-5">
                <div class="md:flex md:items-center md:justify-between">
                    <div class="flex-1 min-w-0">
                        <h1 class="py-0.5 text-2xl font-semibold text-gray-900">{{ $t('Create product') }}</h1>
                    </div>
                </div>
            </div>
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="mt-6 shadow sm:rounded-lg">
                    <loading :status="loading"/>
                    <div class="bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                        <!-- General -->
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Product details') }}</h3>
                            <p class="mt-1 text-sm leading-5 text-gray-500">
                                {{ $t('Product information and assignment') }}.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="grid grid-cols-6 gap-6">
                                <div class="col-span-6 sm:col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="name">{{ $t('Name') }}</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input
                                            id="name"
                                            v-model="product.name"
                                            :placeholder="$t('Name')"
                                            class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                            required
                                        >
                                    </div>
                                </div>
                                <div class="col-span-6 sm:col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="serial_number">{{ $t('Serial number') }}</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input
                                            id="serial_number"
                                            v-model="product.serial_number"
                                            :placeholder="$t('Serial number')"
                                            class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                            required
                                        >
                                    </div>
                                </div>
                                <div class="col-span-6 sm:col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="frontend_id">{{ $t('Frontend') }}</label>
                                    <select
                                        id="frontend_id"
                                        v-model="product.frontend_id"
                                        class="form-select block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5 mt-1"
                                        required
                                    >
                                        <option :value="null" disabled>{{ $t('Select frontend') }}</option>
                                        <option v-for="frontend in formData.frontends" :value="frontend.id">{{ frontend.name }}</option>
                                    </select>
                                </div>
                                <div class="col-span-6 sm:col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="category_id">{{ $t('Category') }}</label>
                                    <select
                                        id="category_id"
                                        v-model="product.category_id"
                                        class="form-select block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5 mt-1"
                                    >
                                        <option :value="null">{{ $t('None') }}</option>
                                        <option v-for="category in formData.categories" :value="category.id">
                                            {{ category.parent_name ? category.parent_name + ' → ' : '' }}{{ category.name }}
                                        </option>
                                    </select>
                                </div>
                                <div class="col-span-6">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="description">{{ $t('Description') }}</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <textarea
                                            id="description"
                                            v-model="product.description"
                                            rows="3"
                                            class="form-textarea block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                        ></textarea>
                                    </div>
                                </div>
                                <div class="col-span-6 sm:col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="support_expiration_date">{{ $t('Support expiration date') }}</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input
                                            id="support_expiration_date"
                                            v-model="product.support_expiration_date"
                                            type="date"
                                            class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                        >
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!-- Assignment -->
                        <div class="md:col-span-3">
                            <div class="py-3">
                                <div class="border-t border-gray-200"></div>
                            </div>
                        </div>
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Assignment') }}</h3>
                            <p class="mt-1 text-sm leading-5 text-gray-500">
                                {{ $t('Assign product to client and partner') }}.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="grid grid-cols-6 gap-6">
                                <div class="col-span-6 sm:col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="client_id">{{ $t('Client') }}</label>
                                    <div class="mt-1 flex items-center gap-2">
                                        <select
                                            id="client_id"
                                            v-model="product.client_id"
                                            class="form-select block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                        >
                                            <option :value="null">{{ $t('None') }}</option>
                                            <option v-for="client in filteredClients" :value="client.id">{{ client.name }} ({{ client.email }})</option>
                                        </select>
                                        <button
                                            type="button"
                                            class="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none"
                                            @click="openQuickCreateModal('client')"
                                        >+</button>
                                    </div>
                                </div>
                                <div class="col-span-6 sm:col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="partner_id">{{ $t('Partner') }}</label>
                                    <div class="mt-1 flex items-center gap-2">
                                        <select
                                            id="partner_id"
                                            v-model="product.partner_id"
                                            class="form-select block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                        >
                                            <option :value="null">{{ $t('None') }}</option>
                                            <option v-for="partner in filteredPartners" :value="partner.id">{{ partner.name }} ({{ partner.email }})</option>
                                        </select>
                                        <button
                                            type="button"
                                            class="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none"
                                            @click="openQuickCreateModal('partner')"
                                        >+</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="bg-gray-100 text-right px-4 py-3 sm:px-6">
                        <span class="inline-flex">
                            <router-link
                                class="btn btn-secondary shadow-sm rounded-md mr-4"
                                to="/dashboard/admin/products"
                            >
                                {{ $t('Cancel') }}
                            </router-link>
                            <button
                                class="btn btn-green shadow-sm rounded-md"
                                type="submit"
                            >
                                {{ $t('Create product') }}
                            </button>
                        </span>
                    </div>
                </div>
            </div>
        </form>
        <!-- Quick create user modal -->
        <div v-show="quickCreateModal" class="fixed z-20 inset-0 overflow-y-auto">
            <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <transition
                    duration="300"
                    enter-active-class="ease-out duration-300"
                    enter-class="opacity-0"
                    enter-to-class="opacity-100"
                    leave-active-class="ease-in duration-200"
                    leave-class="opacity-100"
                    leave-to-class="opacity-0"
                >
                    <div v-show="quickCreateModal" class="fixed inset-0 transition-opacity">
                        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
                    </div>
                </transition>
                <span class="hidden sm:inline-block sm:align-middle sm:h-screen"></span>&#8203;
                <transition
                    enter-active-class="ease-out duration-300"
                    enter-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enter-to-class="opacity-100 translate-y-0 sm:scale-100"
                    leave-active-class="ease-in duration-200"
                    leave-class="opacity-100 translate-y-0 sm:scale-100"
                    leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <div
                        v-show="quickCreateModal"
                        aria-labelledby="quick-create-modal-headline"
                        aria-modal="true"
                        class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
                        role="dialog"
                    >
                        <form @submit.prevent="submitQuickCreate">
                            <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <h3 id="quick-create-modal-headline" class="text-lg leading-6 font-medium text-gray-900 mb-4">
                                    {{ quickCreateRole === 'client' ? $t('Create new client') : $t('Create new partner') }}
                                </h3>
                                <div class="space-y-4">
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">{{ $t('Name') }}</label>
                                        <input
                                            v-model="quickCreateForm.name"
                                            type="text"
                                            class="mt-1 form-input block w-full sm:text-sm sm:leading-5"
                                            :placeholder="$t('Name')"
                                            required
                                        >
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">{{ $t('Email') }}</label>
                                        <input
                                            v-model="quickCreateForm.email"
                                            type="email"
                                            class="mt-1 form-input block w-full sm:text-sm sm:leading-5"
                                            :placeholder="$t('Email')"
                                            required
                                        >
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">{{ $t('Password') }}</label>
                                        <input
                                            v-model="quickCreateForm.password"
                                            type="password"
                                            class="mt-1 form-input block w-full sm:text-sm sm:leading-5"
                                            :placeholder="$t('Password')"
                                            required
                                            minlength="6"
                                        >
                                    </div>
                                    <div v-if="selectedFrontendName">
                                        <label class="block text-sm font-medium text-gray-700">{{ $t('Frontend') }}</label>
                                        <input
                                            type="text"
                                            class="mt-1 form-input block w-full sm:text-sm sm:leading-5 bg-gray-100"
                                            :value="selectedFrontendName"
                                            disabled
                                        >
                                    </div>
                                </div>
                            </div>
                            <div class="bg-gray-100 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                <button
                                    type="submit"
                                    class="btn btn-green mr-2 sm:mr-0"
                                    :disabled="quickCreateLoading"
                                >
                                    {{ $t('Create') }}
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-white mr-0 sm:mr-2"
                                    @click="quickCreateModal = false"
                                >
                                    {{ $t('Cancel') }}
                                </button>
                            </div>
                        </form>
                    </div>
                </transition>
            </div>
        </div>
    </main>
</template>

<script>
export default {
    name: "new",
    metaInfo() {
        return {
            title: this.$i18n.t('Create product')
        }
    },
    data() {
        return {
            loading: true,
            formData: {
                frontends: [],
                categories: [],
                clients: [],
                partners: [],
            },
            product: {
                name: null,
                serial_number: null,
                frontend_id: null,
                category_id: null,
                description: null,
                support_expiration_date: null,
                client_id: null,
                partner_id: null,
            },
            quickCreateModal: false,
            quickCreateLoading: false,
            quickCreateRole: 'client',
            quickCreateForm: {
                name: '',
                email: '',
                password: '',
            },
        }
    },
    computed: {
        selectedFrontendName() {
            if (!this.product.frontend_id) return null;
            const frontend = this.formData.frontends.find(f => f.id === this.product.frontend_id);
            return frontend ? frontend.name : null;
        },
        filteredClients() {
            if (!this.product.frontend_id) return this.formData.clients;
            return this.formData.clients.filter(c => c.frontend_id === this.product.frontend_id);
        },
        filteredPartners() {
            if (!this.product.frontend_id) return this.formData.partners;
            return this.formData.partners.filter(p => p.frontend_id === this.product.frontend_id);
        },
    },
    watch: {
        'product.frontend_id': function () {
            this.product.client_id = null;
            this.product.partner_id = null;
        },
    },
    mounted() {
        this.getFormData();
    },
    methods: {
        saveProduct() {
            const self = this;
            self.loading = true;
            axios.post('api/dashboard/admin/products', self.product).then(function (response) {
                self.loading = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data saved correctly').toString(),
                    type: 'success'
                });
                self.$router.push('/dashboard/admin/products/' + response.data.product.id + '/edit');
            }).catch(function () {
                self.loading = false;
            });
        },
        getFormData() {
            const self = this;
            self.loading = true;
            axios.get('api/dashboard/admin/products/form-data').then(function (response) {
                self.formData = response.data;
                self.loading = false;
            }).catch(function () {
                self.loading = false;
            });
        },
        openQuickCreateModal(role) {
            if (!this.product.frontend_id) {
                this.$notify({
                    title: this.$i18n.t('Warning').toString(),
                    text: this.$i18n.t('Please select a frontend first').toString(),
                    type: 'warn'
                });
                return;
            }
            this.quickCreateRole = role;
            this.quickCreateForm = { name: '', email: '', password: '' };
            this.quickCreateModal = true;
        },
        submitQuickCreate() {
            const self = this;
            self.quickCreateLoading = true;
            const payload = {
                name: self.quickCreateForm.name,
                email: self.quickCreateForm.email,
                password: self.quickCreateForm.password,
                role: self.quickCreateRole,
                frontend_id: self.product.frontend_id,
            };
            axios.post('api/dashboard/admin/products/quick-create-user', payload).then(function (response) {
                self.quickCreateLoading = false;
                self.quickCreateModal = false;
                const newUser = response.data.user;
                if (self.quickCreateRole === 'client') {
                    self.formData.clients.push(newUser);
                    self.product.client_id = newUser.id;
                } else {
                    self.formData.partners.push(newUser);
                    self.product.partner_id = newUser.id;
                }
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data saved correctly').toString(),
                    type: 'success'
                });
            }).catch(function () {
                self.quickCreateLoading = false;
            });
        },
    }
}
</script>
